import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import '@avaldigitallabs/bavv-commons-lib-design-system/dist/bavv-upload/elements.js';
import { BackButtonService } from 'src/app/services/biometrics/back-button.service';
import { BiometricsDataService } from 'src/app/services/biometrics/biometrics-data.service';
import { ImageValidatorService } from 'src/app/services/biometrics/imageValidator.service';
import {
  FILE_TYPE_PARAM,
  MAX_SIZE_MB
} from 'src/app/shared/data/biometrics.constants';
import {
  EBiometricsType,
  EscreenKeys,
  HelperTexts,
  IBiometricsUpload,
  SimpleUploadStatus,
  UStatus
} from 'src/app/shared/interfaces/biometrics.interface';

const mimeMap = new Map([
  ['.jpg', 'image/jpeg'],
  ['.png', 'image/png'],
  ['.pdf', 'application/pdf']
]);

@Component({
  selector: 'app-biometrics-upload',
  templateUrl: './biometrics-upload.component.html',
  styleUrls: ['./biometrics-upload.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BiometricsUploadComponent implements OnInit {
  helperTexts: HelperTexts = this.biometricsDataService.helperTexts;

  type: EBiometricsType;
  upload_texts: IBiometricsUpload[EBiometricsType];
  fileStatus: SimpleUploadStatus = {
    text: '',
    status: UStatus.INITIAL,
    file: null,
    types: '.jpg',
    Image: ''
  };

  validTypes: string[] = [];
  screenKey = EscreenKeys.UPLOAD;

  constructor(
    private activatedRoute: ActivatedRoute,
    private biometricsDataService: BiometricsDataService,
    private router: Router,
    private backButtonService: BackButtonService,
    private imageValidatorService: ImageValidatorService
  ) {
    this.type = this.activatedRoute.snapshot.paramMap.get(
      FILE_TYPE_PARAM
    ) as EBiometricsType;
    this.upload_texts = this.biometricsDataService.biometricsUpload[this.type];
    this.helperTexts = {
      ...this.helperTexts,
      initial: {
        text: this.biometricsDataService.biometricsUpload[this.type]
          .uploadComponent.initial
      },
      failed: {
        text: this.biometricsDataService.biometricsUpload[this.type]
          .uploadComponent.failed
      }
    };
  }

  ngOnInit() {
    this.validTypes = this.fileStatus.types
      .split(',')
      .filter((t) => t !== undefined)
      .map((t) => mimeMap.get(t));
    this.preloadData();
    this.backButtonService.checkScreen(this.type, this.screenKey);
  }

  preloadData() {
    if (this.biometricsDataService.resultsFromBiometrics[this.type]) {
      this.fileStatus = this.biometricsDataService.resultsFromBiometrics[
        this.type
      ] as SimpleUploadStatus;
    }
  }

  onChange() {
    this.startValidations();
  }

  async startValidations() {
    this.fileStatus = await this.checkFile(this.fileStatus.file);
  }

  async checkFile(file?: File | null): Promise<SimpleUploadStatus> {
    if (!file) {
      return {
        ...this.fileStatus,
        status: UStatus.INITIAL,
        text: ''
      };
    }

    if (!this.validTypes.includes(file.type)) {
      return {
        ...this.fileStatus,
        status: UStatus.FAILED,
        text: this.biometricsDataService.uploadScreenErrors.type
      };
    }

    if (file.size / Math.pow(1024, 2) > MAX_SIZE_MB) {
      return {
        ...this.fileStatus,
        status: UStatus.FAILED,
        text: this.biometricsDataService.uploadScreenErrors.size
      };
    }

    if (!(await this.imageValidatorService.validateImage(file))) {
      return {
        ...this.fileStatus,
        status: UStatus.FAILED,
        text: this.biometricsDataService.uploadScreenErrors.format
      };
    }

    return {
      ...this.fileStatus,
      text: '',
      status: UStatus.SUCCESS
    };
  }

  continue() {
    if (this.fileStatus.file) {
      this.imageValidatorService
        .convertToBase64(this.fileStatus.file)
        .then((result: string) => {
          this.fileStatus.Image = result;
          this.biometricsDataService.resultsFromBiometrics[
            this.type.toString()
          ] = this.fileStatus;
          this.router.navigate([
            this.biometricsDataService.routingBiometrics[this.type][
              this.screenKey
            ].to
          ]);
        });
    }
  }

  onDelete() {
    this.fileStatus = {
      ...this.fileStatus,
      status: UStatus.INITIAL,
      file: null,
      Image: ''
    };
  }
}
